import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, message, Modal, Popconfirm, Space, Table, Checkbox, Input, AutoComplete, Select, Row, Col, Spin } from 'antd';
import AddUser from './AddUser';
import AddUserPermission from './AddUserPermission';
import CompanyService from '../../../services/Admin/Company';
import { RoleOptions } from '../../../constants/roles';

const { Option } = Select;

interface UserListProps {
    companyId?: string;
}

const UserList: FunctionComponent<UserListProps> = ({ companyId: companyIdProp }) => {
    let { id: companyIdParam } = useParams<any>();
    const companyId = companyIdProp || companyIdParam;

    const [loading, setLoading] = useState<boolean>(true);
    const [autocompleteUsers, setAutocompleteUsers] = useState<Array<any>>([]);
    const [adduserModalVisible, setAddUserModalVisible] = useState<boolean>(false);
    const [userTable, setUserTable] = useState<any>({
        currentPageNumber: 1,
        users: [],
    });
    const [totalUserCount, setTotalUserCount] = useState(0);
    const [roleModal, setRoleModal] = useState<any>({
        visible: false,
        confirmMessage: '',
        userId: 0,
        newRoleId: -1,
    });
    const [permissionModal, setPermissionModal] = useState<any>({
        userId: 0,
        visible: false,
    });
    const { t } = useTranslation(['f']);
    const userPageSize = 20;

    useEffect(() => {
        if (companyId) {
            fetchUsersInCompany(companyId, 0);
        }
    }, [companyId]);

    async function handleAddUser(userId) {
        if (userId == 0) return;
        await CompanyService.addUsersToCompany(companyId, [userId]);
        setAutocompleteUsers([]);
        fetchUsersInCompany(companyId, userTable.currentPageNumber - 1);
    }

    function fetchUsersInCompany(companyId, pageIdx: number) {
        setLoading(true);
        CompanyService.fetchCompanyUsers(companyId, pageIdx, userPageSize).then((resp) => {
            setLoading(false);
            if (resp && resp.items) {
                setUserTable({
                    currentPageNumber: pageIdx + 1,
                    users: [...resp.items],
                });
                setTotalUserCount(resp.totalCount);
            }
        });
    }

    async function handleActive(id: any) {}

    async function handleRemoveUser(userId) {
        await CompanyService.removeUsersFromCompany(companyId, [userId]);
        fetchUsersInCompany(companyId, userTable.currentPageNumber - 1);
    }

    async function handleDeleteUser(userId) {
        await CompanyService.deleteUser(userId);
        fetchUsersInCompany(companyId, userTable.currentPageNumber - 1);
    }

    async function handleResendInvite(userId) {
        try {
            const result = await CompanyService.resendInvitation(userId);
            if (result?.success === false) {
                message.error(`Email failed: ${result.error}`, 8);
            } else {
                message.success('Invitation email resent.');
            }
        } catch {
            message.error('Failed to resend invitation.');
        }
    }

    async function handleAssignRoleToUser() {
        try {
            await CompanyService.assignRoleToUserInCompany(companyId, roleModal.userId, roleModal.newRoleId);
            setRoleModal({ visible: false, confirmMessage: '', userId: 0, newRoleId: -1 });
            fetchUsersInCompany(companyId, userTable.currentPageNumber - 1);
        } catch {
            message.error('Failed to update role.');
        }
    }

    async function handleCopyInviteLink(userId) {
        try {
            const link = await CompanyService.getInvitationLink(userId);
            await navigator.clipboard.writeText(link);
            message.success('Invitation link copied to clipboard.');
        } catch {
            message.error('Failed to generate invitation link.');
        }
    }

    const openAddUserModal = () => {
        setAddUserModalVisible(true);
    };

    function getRoleName(roleId) {
        return roleId == 1 ? 'Admin' : roleId == 0 ? 'Member' : 'Manager';
    }

    function handleUsersPageChange(pageNumber, pageSize) {
        fetchUsersInCompany(userTable.companyId, pageNumber - 1);
    }

    async function userAdded() {
        setAddUserModalVisible(false);
        fetchUsersInCompany(companyId, 0);
    }

    async function cancelAddUserModal() {
        setAddUserModalVisible(false);
    }

    async function searchUsers(e) {
        let keyword = e.target.value;

        if (keyword.length >= 3) {
            const users = await CompanyService.searchUsersNotInCompany(companyId, keyword);
            const userOptions = users.map((u) => ({
                id: u.id,
                fullName: u.fullName,
                value: u.email,
                label: (
                    <div>
                        <Popconfirm title="Are you sure ?" onConfirm={() => handleAddUser(u.id)}>
                            <Button type="primary" style={{ marginRight: 15 }}>
                                Add
                            </Button>
                        </Popconfirm>
                        {u.email}
                    </div>
                ),
            }));

            if (userOptions.length == 0) {
                userOptions.push({
                    id: 0,
                    label: 'Not found users',
                    value: '',
                });
            }

            setAutocompleteUsers([...userOptions]);
        }
    }

    const handleContainer = useCallback(() => {
        const theme = document.getElementById('theme');
        return theme ? theme : document.body;
    }, []);

    const userColumns = [
        {
            title: 'User Name',
            dataIndex: 'userName',
            key: 'userName',
            width: 180,
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            width: 200,
        },
        {
            title: 'Full Name',
            dataIndex: 'fullName',
            key: 'fullName',
            width: 140,
        },
        {
            title: 'Role',
            key: 'role',
            width: 150,
            render: (_, record) =>
                record.role != 3 ? (
                    <Space size="middle">
                        <Select
                            defaultValue={record.role}
                            style={{ width: 130 }}
                            value={record.role}
                            onSelect={(value) => {
                                if (value != record.role) {
                                    setRoleModal({
                                        visible: true,
                                        userId: record.id,
                                        newRoleId: value,
                                        confirmMessage: `Do you want to assign role from ${getRoleName(
                                            record.role
                                        )} to ${getRoleName(value)} for ${record.email} ?`,
                                    });
                                }
                            }}
                            getPopupContainer={handleContainer}
                        >
                            {RoleOptions.map((roleValue) => (
                                    <Option key={roleValue} value={roleValue}>{t(`f:userRoles.${roleValue}`)}</Option>
                                ))}
                        </Select>
                    </Space>
                ) : (
                    <Space size="middle">
                        <Select
                            defaultValue={record.role}
                            style={{ width: 130 }}
                            disabled
                            getPopupContainer={handleContainer}
                        >
                            <Option value={3}>Super Admin</Option>
                        </Select>
                    </Space>
                ),
        },
        {
            title: 'Active',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 80,
            render: (_, record) => (
                <Space size="middle">
                    <Popconfirm title="Are you sure ?" onConfirm={() => handleActive(record.id)}>
                        <Checkbox checked={record.isActive}></Checkbox>
                    </Popconfirm>
                </Space>
            ),
        },
        {
            title: 'Action',
            key: 'action',
            width: 420,
            fixed: 'right' as const,
            render: (_, record) => (
                <Space size="small" wrap>
                    <Button onClick={() => handleResendInvite(record.id)}>Resend Invite</Button>
                    <Button onClick={() => handleCopyInviteLink(record.id)}>Copy Link</Button>
                    <Button onClick={() => setPermissionModal({ userId: record.id, visible: true })}>Permission</Button>
                    <Popconfirm title="Remove user from company?" onConfirm={() => handleRemoveUser(record.id)}>
                        <Button danger={true}>Remove</Button>
                    </Popconfirm>
                    <Popconfirm title="Permanently delete this user?" onConfirm={() => handleDeleteUser(record.id)}>
                        <Button danger={true} type="primary">Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <Button onClick={openAddUserModal} type="primary" size="large">
                    + Invite New User
                </Button>
                <AutoComplete
                    onBlur={() => setAutocompleteUsers([])}
                    open={autocompleteUsers.length > 0}
                    onKeyUp={searchUsers}
                    dropdownMatchSelectWidth={500}
                    style={{ flex: 1, maxWidth: 480 }}
                    notFoundContent="Not Found Users"
                    options={autocompleteUsers}
                >
                    <Input.Search
                        size="large"
                        placeholder="Re-add a removed user (type 3+ characters)"
                    />
                </AutoComplete>
            </div>
            <Spin tip="Loading..." spinning={loading}>
                <Table
                    rowClassName={() => 'editable-row'}
                    bordered
                    dataSource={userTable.users}
                    columns={userColumns}
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        onChange: handleUsersPageChange,
                        total: totalUserCount,
                        pageSize: userPageSize,
                    }}
                />
            </Spin>
            <AddUser
                companyId={companyId}
                isModalOpen={adduserModalVisible}
                onFinish={userAdded}
                onCancel={cancelAddUserModal}
            ></AddUser>
            <AddUserPermission
                userId={permissionModal.userId}
                companyId={companyId}
                isModalOpen={permissionModal.visible}
                onCancel={() => setPermissionModal({ userId: -1, visible: false })}
            ></AddUserPermission>
            <Modal
                title="Change Role"
                open={roleModal.visible}
                onOk={handleAssignRoleToUser}
                onCancel={() => setRoleModal({ visible: false, confirmMessage: '', userId: 0, newRoleId: -1 })}
                okText="Save"
                okButtonProps={{ danger: false }}
            >
                {roleModal.confirmMessage}
            </Modal>
        </>
    );
};

export default UserList;
